import React, { useMemo, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  FormControl,
  Divider,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardDoubleArrowDownRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowDownRounded";

const FORMAS_PAGO = [
  { codigo: "20", nombre: "Otros con Utilización del Sistema Financiero" },
  { codigo: "01", nombre: "Sin utilización del Sistema Financiero" },
  { codigo: "15", nombre: "Compensación de Deudas" },
  { codigo: "16", nombre: "Tarjeta de débito" },
  { codigo: "17", nombre: "Dinero Electrónico" },
  { codigo: "18", nombre: "Tarjeta Prepago" },
  { codigo: "19", nombre: "Tarjeta de crédito" },
  { codigo: "21", nombre: "Endoso de títulos" },
];

const toMoney2 = (n) => {
  const x = Number(n || 0);
  return Number.isFinite(x) ? x.toFixed(2) : "0.00";
};

const defaultRow = (valor = "") => ({
  forma_pago: "20",
  plazo: "0",
  unidad_tiempo: "dias",
  valor,
});

const cleanMoneyInput = (value) => {
  const v = String(value ?? "").replace(",", ".");

  if (v === "") return "";

  const match = v.match(/^\d*(?:\.\d{0,2})?$/);

  if (match) return v;

  const only = v.replace(/[^\d.]/g, "");
  const parts = only.split(".");

  if (parts.length === 1) return parts[0];

  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

export default function InvoicePayments({ totalPagar, payments, setPayments }) {
  const safePayments = Array.isArray(payments) ? payments : [];
  const totalActual = toMoney2(totalPagar);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;

    if (!safePayments.length && Number(totalActual) > 0) {
      setPayments([defaultRow(totalActual)]);
      initializedRef.current = true;
    }
  }, [totalActual, safePayments.length, setPayments]);

  const rows = safePayments.length ? safePayments : [defaultRow("")];

  const totalAsignado = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.valor) || 0), 0),
    [rows]
  );

  const isMatch = totalActual === totalAsignado.toFixed(2);

  const addRow = () => {
    setPayments([...rows, defaultRow("")]);
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    setPayments(next.length ? next : [defaultRow("")]);
  };

  const updateRow = (idx, patch) => {
    setPayments(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const setTotalInFirstRow = () => {
    if (!rows.length) {
      setPayments([defaultRow(totalActual)]);
      return;
    }

    setPayments(
      rows.map((r, i) =>
        i === 0
          ? {
              ...r,
              valor: totalActual,
            }
          : r
      )
    );
  };

  return (
    <Box>
      <Paper sx={{ borderRadius: 2, border: "1px solid #e6e6e6" }}>
        <Box sx={{ bgcolor: "#f3f3f3", px: 2, py: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <Typography fontWeight={700}>Forma de pago</Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Plazo</Typography>
            </Grid>

            <Grid item xs={12} md={2.5}>
              <Typography fontWeight={700}>Unid. de tiempo</Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Valor</Typography>
            </Grid>

            <Grid item xs={12} md={0.5} />
          </Grid>
        </Box>

        <Box sx={{ p: 2 }}>
          {rows.map((r, idx) => (
            <Grid
              container
              spacing={1.5}
              key={idx}
              alignItems="center"
              sx={{ mb: idx === rows.length - 1 ? 0 : 2 }}
            >
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <Select
                    value={r.forma_pago}
                    onChange={(e) => updateRow(idx, { forma_pago: e.target.value })}
                  >
                    {FORMAS_PAGO.map((f) => (
                      <MenuItem key={f.codigo} value={f.codigo}>
                        {f.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  value={r.plazo}
                  onChange={(e) =>
                    updateRow(idx, {
                      plazo: e.target.value.replace(/\D/g, "").slice(0, 4) || "0",
                    })
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth>
                  <Select
                    value={r.unidad_tiempo}
                    onChange={(e) => updateRow(idx, { unidad_tiempo: e.target.value })}
                  >
                    <MenuItem value="dias">días</MenuItem>
                    <MenuItem value="meses">meses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  value={r.valor}
                  onChange={(e) =>
                    updateRow(idx, {
                      valor: cleanMoneyInput(e.target.value),
                    })
                  }
                  inputProps={{ inputMode: "decimal" }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={0.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton onClick={() => removeRow(idx)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addRow}
            >
              Agregar detalle
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            alignItems: "center",
          }}
        >
          <Box textAlign="right">
            <Typography fontWeight={700}>Total a pagar</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography>${totalActual}</Typography>
              <Tooltip title="Colocar total en primera fila">
                <IconButton size="small" onClick={setTotalInFirstRow}>
                  <KeyboardDoubleArrowDownRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box textAlign="right">
            <Typography fontWeight={700}>Total asignado</Typography>
            <Typography sx={{ color: isMatch ? "#2e7d32" : "#b71c1c" }}>
              ${totalAsignado.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}